import { contentAssetService } from 'content/library/service/contentAssetService';
import { VideoAssetSet } from 'content/library/types/contentVideoType';
import { videoService } from 'service/api/videoService';
import { videoSubtitleService } from 'src/domain/video/assets/services/videoSubtitleService';
import { VideoSubtitleType } from 'type/videoSubtitleType';

export class VideoAssetsService {
  /**
   * Fetch content video assets
   */
  static async fetchContentVideoAssets(contentId: number): Promise<VideoAssetSet[]> {
    return await contentAssetService.fetchContentVideoAssets(contentId);
  }

  /**
   * Fetch content video asset history
   */
  static async fetchContentVideoAssetHistory(contentId: number) {
    return await contentAssetService.fetchContentVideoAssetHistory(contentId);
  }

  /**
   * Save new content video assets
   */
  static async saveContentVideoAssets(contentId: number, assetSets: VideoAssetSet[]) {
    return await contentAssetService.saveContentVideoAssets(contentId, assetSets);
  }

  /**
   * Update existing content video asset
   */
  static async updateContentVideoAsset(assetId: number, data: Partial<VideoAssetSet>) {
    return await contentAssetService.updateContentVideoAsset(assetId, data);
  }

  /**
   * Delete content video asset
   */
  static async deleteContentVideoAsset(assetId: number) {
    return await contentAssetService.deleteContentVideoAsset(assetId);
  }

  /**
   * Search videos with filters
   */
  static async searchVideos(params: {
    search?: string;
    source?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.source) searchParams.append('ingest_source', params.source);
    if (params.status) searchParams.append('status', params.status);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('page_size', params.pageSize.toString());

    return await videoService.fetchVideos(searchParams);
  }

  /**
   * Fetch subtitles for a video
   */
  static async fetchVideoSubtitles(videoId: number): Promise<VideoSubtitleType[]> {
    return await videoSubtitleService.fetchByVideoId(videoId);
  }

  /**
   * Fetch subtitles for multiple videos in parallel
   */
  static async fetchMultipleVideoSubtitles(
    videoIds: number[]
  ): Promise<{ [videoId: number]: VideoSubtitleType[] }> {
    const subtitlePromises = videoIds.map(async videoId => {
      try {
        const subtitles = await videoSubtitleService.fetchByVideoId(videoId);
        return { videoId, subtitles };
      } catch (error) {
        console.error(`Error fetching subtitles for video ${videoId}:`, error);
        return { videoId, subtitles: [] };
      }
    });

    const subtitleResults = await Promise.all(subtitlePromises);
    const videoSubtitlesMap: { [videoId: number]: VideoSubtitleType[] } = {};
    subtitleResults.forEach(({ videoId, subtitles }) => {
      videoSubtitlesMap[videoId] = subtitles;
    });

    return videoSubtitlesMap;
  }

  /**
   * Trigger video transcoding
   */
  static triggerVideoTranscoding(
    contentId: number,
    videoId: number,
    subtitleIds: number[] | null,
    serviceName: string,
    assetId: number
  ) {
    return videoService.videoTranscode(contentId, videoId, subtitleIds, serviceName, assetId);
  }
}
